import {
  APPOINTMENT_STATUS_ARRIVED,
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_NO_SHOW
} from "constants/appointments";
import {
  Appointment,
  type AppointmentAssignedTagsInput,
  AppointmentResponse,
  AppointmentSkeleton,
  type TinyPatientAppointmentResponse
} from "services/types/appointments.type";
import { Diagnosis } from "services/types/diagnosis.type";
import { SubReferralSource } from "services/types/referral-source.type";
import { formatPhysician } from "utils/physicians.utils";

import { formatClinic } from "./clinics.util";
import {
  convertDateInTimezoneToCurrent,
  formatStaffMemberFullName
} from "./common";
import { formatPatient } from "./patients.util";
import { formatRoomClinic } from "./rooms.util";

export function formatActualDuration(minutes: number | undefined): string {
  if (!minutes || minutes === 0) return "0";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
}

export function convertToAppointmentInput(
  appointmentData: Partial<Omit<Appointment, "diagnosis">> &
    Partial<{
      diagnosis: Partial<Diagnosis>;
      assigned_tags?: AppointmentAssignedTagsInput;
    }>
) {
  let subReferralSource:
    | SubReferralSource
    | string
    | number
    | null
    | undefined = undefined;
  if (appointmentData?.subReferralSource) {
    subReferralSource =
      typeof appointmentData?.subReferralSource === "object"
        ? appointmentData?.subReferralSource?.id ||
          appointmentData?.subReferralSource
        : appointmentData?.subReferralSource;
  }

  //we need to truncate current time zone info here
  const start_moment = appointmentData?.startDate;

  const end_moment = appointmentData?.endDate;
  const referral_source =
    appointmentData?.referralSource === null
      ? null
      : appointmentData?.referralSource &&
          typeof appointmentData.referralSource === "object"
        ? appointmentData.referralSource.id
        : appointmentData?.referralSource || undefined;
  const emptyValueFallback = referral_source !== undefined ? null : undefined;

  return {
    status: appointmentData?.status,
    title: appointmentData?.title,
    patient: appointmentData?.patient?.id,
    type: appointmentData?.appointmentType?.id,
    staff_member: appointmentData?.staffMemberId,
    start_moment,
    end_moment,
    room: appointmentData?.room?.id,
    clinic: appointmentData?.clinic?.id,
    physician_referral: appointmentData?.referral,
    referral_source: referral_source,
    sub_referral_source: subReferralSource || emptyValueFallback,
    sub_referral_source_content_type: appointmentData?.subReferralSourceObject
      ? appointmentData?.subReferralSourceObject?.content_type ||
        emptyValueFallback
      : emptyValueFallback,
    sub_referral_source_object_id: appointmentData?.subReferralSourceObject
      ? appointmentData?.subReferralSourceObject?.id || emptyValueFallback
      : emptyValueFallback,
    companion_name: appointmentData?.companionName,
    companion_type: appointmentData?.companionType,
    should_notify: appointmentData?.shouldNotify,
    should_create_schedule_for_staff:
      appointmentData.shouldCreateScheduleForStaff,
    notes: appointmentData?.notes,
    outcome: appointmentData?.outcome,
    outcome_reason: appointmentData?.outcome_reason
      ? appointmentData?.outcome_reason?.id
      : appointmentData?.outcome
        ? null
        : undefined,
    outcome_notes: appointmentData?.outcome_notes,
    chart_note: appointmentData?.chartNote,
    allow_overlap: appointmentData?.allowOverlap,
    referring_physician:
      typeof appointmentData?.referringPhysician === "object"
        ? appointmentData?.referringPhysician?.id ||
          appointmentData?.referringPhysician
        : appointmentData?.referringPhysician,
    notification_method: appointmentData?.notificationMethod,
    should_create_intake_form_request:
      appointmentData?.should_create_intake_form_request,
    intake_form_request: {
      intake_form_request_submissions: {
        create:
          appointmentData?.intake_form_request?.intake_form_request_submissions
            ?.create || []
      },
      notification_method:
        appointmentData?.intake_form_request?.notification_method
    },
    diagnosis: appointmentData?.diagnosis,
    hearing_test_conducted: appointmentData?.hearing_test_conducted,
    companion_present: appointmentData?.companion_present,
    hearing_aid_demo_performed: appointmentData?.hearing_aid_demo_performed,
    telehealth_provider: appointmentData?.telehealth_provider,
    rescheduled_appointment_id: appointmentData?.rescheduled_appointment_id,
    assigned_tags: appointmentData.assigned_tags,
    schedule: appointmentData.schedule,
    start_recurrence_id: appointmentData.start_recurrence_id,
    supervising_provider:
      typeof appointmentData?.supervising_provider === "object"
        ? appointmentData?.supervising_provider?.id || null
        : appointmentData?.supervising_provider,
    is_direct_supervisor: appointmentData?.is_direct_supervisor,
    created_from_appointment:
      typeof appointmentData?.created_from_appointment === "object"
        ? appointmentData?.created_from_appointment?.id
        : appointmentData?.created_from_appointment
  } as Partial<AppointmentSkeleton>;
}
export function convertAppointmentResponseToModel(
  appointment: AppointmentResponse
) {
  const timeZone = appointment.clinic?.timezone;
  return {
    id: appointment.id,
    podium_id: appointment.podium_id,
    podium_sync: appointment.podium_sync,
    title: appointment.title,
    type: "Appointment",
    patient: appointment.patient && formatPatient(appointment.patient),
    start_moment: appointment.start_moment,
    end_moment: appointment.end_moment,
    startDate:
      appointment.start_moment &&
      convertDateInTimezoneToCurrent(appointment.start_moment, timeZone),
    endDate:
      appointment.end_moment &&
      convertDateInTimezoneToCurrent(appointment.end_moment, timeZone),
    staffMember:
      appointment.staff_member &&
      formatStaffMemberFullName(appointment.staff_member),
    staffMemberId: appointment.staff_member?.id,
    appointmentType: appointment.type,
    room: appointment.room && formatRoomClinic(appointment.room),
    clinic: appointment.clinic && formatClinic(appointment.clinic),
    referral: appointment.physician_referral,
    referralSource: appointment.referral_source,
    subReferralSource: appointment.sub_referral_source,
    referringPhysician:
      appointment.referring_physician &&
      typeof appointment.referring_physician === "object"
        ? formatPhysician(appointment.referring_physician)
        : appointment.referring_physician,
    companionType: appointment.companion_type,
    companionName: appointment.companion_name,
    notes: appointment.notes,
    status: appointment.status,
    chartNote: appointment.chart_note,
    shouldNotify: appointment.should_notify,
    should_create_intake_form_request:
      appointment.should_create_intake_form_request,
    outcome: appointment.outcome,
    outcome_display: appointment.outcome_display,
    outcome_reason: appointment.outcome_reason,
    outcome_notes: appointment.outcome_notes,
    shouldCreateScheduleForStaff: appointment.should_create_schedule_for_staff,
    overwriteBlock: appointment.overwrite_block,
    webPatient: appointment.extra,
    assignedTags: appointment.assigned_tags,
    patient_assigned_tags: appointment.patient_assigned_tags,
    patientFeedbackRating: appointment.patient_feedback_rating,
    patient_feedback_text: appointment.patient_feedback_text,
    needs_reschedule: appointment.needs_reschedule,
    rescheduled_appointment_id: appointment.rescheduled_appointment_id,
    replacement_appointment: appointment.replacement_appointment,
    crudEvents: appointment?.crud_events?.map((event) => {
      const object_json_repr = JSON.parse(event.object_json_repr)[0].fields;

      return {
        ...event,
        object_json_repr,
        user: event.user && formatStaffMemberFullName(event.user)
      };
    }),
    companion_present: appointment.companion_present,
    hearing_aid_demo_performed: appointment.hearing_aid_demo_performed,
    companion_type_display: appointment.companion_type_display,
    telehealth_provider: appointment.telehealth_provider,
    notificationMethod: appointment.notification_method,
    diagnosis: appointment.diagnosis,
    hearing_test_conducted: appointment.hearing_test_conducted,
    next_appointment_start_moment: appointment?.next_appointment_start_moment,
    next_appointment_start_date_display:
      appointment?.next_appointment_start_date_display,
    created_by: appointment?.created_by,
    created_at_display: appointment?.created_at_display,
    schedule: appointment?.schedule,
    start_time: appointment?.start_time,
    end_time: appointment?.end_time,
    supervising_provider: appointment?.supervising_provider
      ? formatStaffMemberFullName(appointment.supervising_provider)
      : null,
    is_direct_supervisor: appointment?.is_direct_supervisor,
    duration: appointment?.duration,
    actual_duration: appointment?.actual_duration,
    timer: appointment?.timer
  } as Appointment;
}

export const getNextAppointmentMomentData = (params: {
  original_next_appointment: TinyPatientAppointmentResponse | null | undefined;
  original_next_appointment_moment: string | null | undefined;
  following_appointment: TinyPatientAppointmentResponse | null | undefined;
  following_appointment_moment: string | null | undefined;
}) => {
  const {
    original_next_appointment,
    original_next_appointment_moment,
    following_appointment,
    following_appointment_moment
  } = params;

  const show_following_appointment = Boolean(
    original_next_appointment?.status === APPOINTMENT_STATUS_ARRIVED &&
      following_appointment_moment
  );

  if (
    (original_next_appointment?.status === APPOINTMENT_STATUS_NO_SHOW ||
      original_next_appointment?.status === APPOINTMENT_STATUS_COMPLETED) &&
    following_appointment &&
    following_appointment_moment
  ) {
    return {
      next_appointment: following_appointment,
      next_appointment_moment: following_appointment_moment,
      show_following_appointment: show_following_appointment
    };
  }
  return {
    next_appointment: original_next_appointment,
    next_appointment_moment: original_next_appointment_moment,
    show_following_appointment: show_following_appointment
  };
};
